/**
 * Improvement conversations — landlord API client (improvement-agent DC-1).
 *
 * Types are derived from the boundary contract in `@resida/api-types/agent`
 * (packages/api-types/src/agent/improvement.ts), the same Zod schemas apps/api registers.
 * Identity is never sent: the session is the landlord and the user (BR-CONV-RELAY-IDENT).
 *
 * Covers:
 *   POST /landlord/api/improvement-conversations
 *   POST /landlord/api/improvement-conversations/:id/messages
 *   GET  /landlord/api/improvement-conversations/:id
 *   POST /landlord/api/improvement-conversations/:id/proposals/:proposalId/confirm
 *   POST /landlord/api/improvement-conversations/:id/proposals/:proposalId/not-quite
 *   POST /landlord/api/improvement-conversations/:id/reroute
 *
 * Errors propagate as `apiFetch`'s `ApiClientError` (`status` 404 gone, 409 conflict, 422 refused,
 * 429 rate limited, 503 runtime unavailable); `improvementErrorKind` maps one onto the UI state.
 */

import type {
  ImprovementConfirmBody,
  ImprovementConfirmResponse,
  ImprovementConversationViewResponse,
  ImprovementMessageResponse,
  ImprovementRerouteBody,
  ImprovementStartBody,
  ImprovementStartResponse,
} from '@resida/api-types/agent';
import { ApiClientError, apiFetch } from '../lib/api.core';

export type ImprovementConversationView = ImprovementConversationViewResponse;
export type ImprovementProposalView = NonNullable<ImprovementConversationView['proposal']>;
export type ImprovementConflictView = NonNullable<ImprovementConversationView['conflict']>;
export type ImprovementOutcome = NonNullable<ImprovementConversationView['outcome']>;

const BASE = '/landlord/api/improvement-conversations';

/** React Query key for one conversation. */
export function improvementConversationQueryKey(conversationId: string) {
  return ['improvement-conversation', conversationId] as const;
}

export async function startImprovementConversation(body: ImprovementStartBody): Promise<ImprovementStartResponse> {
  return apiFetch<ImprovementStartResponse>(BASE, { method: 'POST', body: JSON.stringify(body) });
}

export async function getImprovementConversation(conversationId: string): Promise<ImprovementConversationView> {
  return apiFetch<ImprovementConversationView>(`${BASE}/${encodeURIComponent(conversationId)}`);
}

export async function sendImprovementMessage(
  conversationId: string,
  text: string,
): Promise<ImprovementMessageResponse> {
  return apiFetch<ImprovementMessageResponse>(`${BASE}/${encodeURIComponent(conversationId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export async function confirmImprovementProposal(
  conversationId: string,
  proposalId: string,
  choice?: ImprovementConfirmBody['choice'],
): Promise<ImprovementConfirmResponse> {
  return apiFetch<ImprovementConfirmResponse>(
    `${BASE}/${encodeURIComponent(conversationId)}/proposals/${encodeURIComponent(proposalId)}/confirm`,
    // The conflict choice (BR-12); absent means keep_both.
    { method: 'POST', ...(choice ? { body: JSON.stringify({ choice }) } : {}) },
  );
}

export async function notQuiteImprovementProposal(conversationId: string, proposalId: string): Promise<void> {
  await apiFetch<unknown>(
    `${BASE}/${encodeURIComponent(conversationId)}/proposals/${encodeURIComponent(proposalId)}/not-quite`,
    { method: 'POST' },
  );
}

export async function rerouteImprovementConversation(
  conversationId: string,
  to: ImprovementRerouteBody['to'],
): Promise<ImprovementMessageResponse> {
  return apiFetch<ImprovementMessageResponse>(`${BASE}/${encodeURIComponent(conversationId)}/reroute`, {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
}

export type ImprovementErrorKind = 'gone' | 'rate_limited' | 'unavailable' | 'conflict' | 'refused' | 'unknown';

/** Maps a failed call onto the panel state it shows (design brief: States). */
export function improvementErrorKind(error: unknown): ImprovementErrorKind {
  if (!(error instanceof ApiClientError)) return 'unknown';
  switch (error.status) {
    case 404:
      return 'gone';
    case 409:
      return 'conflict';
    case 422:
      return 'refused';
    case 429:
      return 'rate_limited';
    case 503:
      return 'unavailable';
    default:
      return 'unknown';
  }
}
